import { Button, CircularProgress, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import Box from '@mui/material/Box';
import type { FC } from 'react';
import { useCallback } from 'react';
import { ConfirmationDialog, SelectApplications, SuspenseBoundary } from 'components/common';
import { isEmpty } from 'lodash-es';
import { useEnqueueSnackbar, useModal } from 'hooks';
import { addGoToTestFormValidator } from '../validation';
import type { z } from 'zod';
import { useNavigate } from 'react-router';
import { usePermissionGuard } from 'features/login';
import type { GoToTestDetails } from 'features/cabinet/tiktok/types';
import SelectFlows from './SelectFlows';
import { useAddGoToTest, useUpdateGoToTest } from 'features/cabinet/tiktok/api';
import SelectAccounts from './SelectAccounts';
import DeleteGoToTest from './DeleteGoToTest';
import {
  cabinetDatadogSources,
  createCabinetMutationTracking,
  trackCabinetSubmitAttempt,
} from 'lib/datadog';

type FormData = z.infer<typeof addGoToTestFormValidator.schema>;

interface FormProps {
  data?: GoToTestDetails;
}

const GoToTestForm: FC<FormProps> = ({ data }) => {
  const { accessToDeleteTiktokgototest } =
    usePermissionGuard();
  const { onShowErrorAlert, onShowAlert } = useEnqueueSnackbar();
  const navigate = useNavigate();
  const { open, close, isOpen } = useModal();
  const isEditMode = !isEmpty(data);

  const formMethods = useForm<FormData>({
    resolver: addGoToTestFormValidator.resolver,
    defaultValues: {
      goToTestId: data?.id,
      app: data?.app ?? '',
      flow_id: data?.flow?.id ?? undefined,
      account_id: data?.account?.account_id ?? undefined,
    },
  });

  const {
    handleSubmit,
    formState: { isValid, isDirty },
  } = formMethods;

  const addGoToTest = useAddGoToTest({
    config: createCabinetMutationTracking({
      platform: 'tiktok',
      entity: 'go_to_test',
      mode: 'create',
      onSuccess: () => {
        onShowAlert('GoToTest added successfully');
        navigate('/cabinet/tiktok/go-to-test');
      },
      onError: (error) => {
        onShowErrorAlert(error.message);
      },
    }),
  });

  const updateGoToTest = useUpdateGoToTest({
    config: createCabinetMutationTracking({
      platform: 'tiktok',
      entity: 'go_to_test',
      mode: 'edit',
      onSuccess: () => {
        onShowAlert('GoToTest updated successfully');
        navigate('/cabinet/tiktok/go-to-test');
      },
      onError: (error) => {
        onShowErrorAlert(error.message);
      },
    }),
  });

  const onSubmit = useCallback(
    (data: FormData) => {
      trackCabinetSubmitAttempt(
        'tiktok',
        'go_to_test',
        isEditMode ? 'edit' : 'create',
      );

      const payload = {
        app: data?.app,
        flow_id: data?.flow_id,
        account_id: data?.account_id,
      };

      if (isEditMode) {
        updateGoToTest.mutate({ ...payload, go_to_test_id: Number(data.goToTestId) });
      } else {
        addGoToTest.mutate(payload);
      }
    },
    [addGoToTest, updateGoToTest, isEditMode],
  );

  const isPending = isEditMode ? updateGoToTest.isPending : addGoToTest.isPending && !addGoToTest.isError;

  return (
    <>
      <FormProvider {...formMethods}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            maxWidth: '40%',
            alignItems: 'start',
            justifyContent: 'start',
          }}
        >
          <Typography variant={'subtitle1'}>
            {isEditMode ? 'Edit flow' : 'Add flow'}
          </Typography>
          <SelectApplications required name={'app'} dataTestId={'applications'} />
          <Box sx={{ display: 'flex', width: '100%' }}>
            <SuspenseBoundary
              datadogSource={cabinetDatadogSources.query('tiktok', 'flow')}
            >
              <SelectFlows
                name={'flow_id'}
                dataTestId={'flows'}
              />
            </SuspenseBoundary>
          </Box>
          <Box sx={{ display: 'flex', width: '100%' }}>
            <SuspenseBoundary
              datadogSource={cabinetDatadogSources.query('tiktok', 'account')}
            >
              <SelectAccounts name={'account_id'} dataTestId={'accounts'} />
            </SuspenseBoundary>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Button
              startIcon={
                isPending && <CircularProgress color={'inherit'} size={18} />
              }
              onClick={() => handleSubmit(onSubmit)()}
              variant={'contained'}
              disabled={isPending || !isValid || !isDirty}
            >
              {isEditMode ? 'Update Go To Test' : 'Save Go To Test'}
            </Button>
            {isEditMode && data && (
              <>
                <Button
                  variant={'outlined'}
                  color={'error'}
                  onClick={open}
                  disabled={isPending || !accessToDeleteTiktokgototest}
                >
                  Delete
                </Button>
                {accessToDeleteTiktokgototest && (
                  <DeleteGoToTest
                    isOpen={isOpen}
                    close={close}
                    id={data.id}
                    open={open}
                  />
                )}
              </>
            )}
          </Box>
        </Box>
        <ConfirmationDialog
          isUnblock={addGoToTest.isSuccess || updateGoToTest.isSuccess}
        />
      </FormProvider>
    </>
  );
};

export default GoToTestForm;
